import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { AlertTriangle, CheckCircle2, FileWarning, Banknote, ExternalLink } from "lucide-react";

const SHIFTS_3 = ["SC-01", "SC-02", "SC-03"];
const SHIFTS_2 = ["SC-D", "SC-N"];

export default function Reconciliation() {
  const [date, setDate] = useState(todayISO());
  const [shiftCode, setShiftCode] = useState("SC-01");
  const [recon, setRecon] = useState(null);
  const [summary, setSummary] = useState(null);
  const [shiftMode, setShiftMode] = useState("3-shift");

  useEffect(() => {
    (async () => {
      const cfg = await api.get("/masters/config");
      setShiftMode(cfg.data?.SHIFT_MODE || "3-shift");
    })();
  }, []);

  const shifts = shiftMode === "2-shift" ? SHIFTS_2 : SHIFTS_3;

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/uploads/recon", { params: { date, shift_code: shiftCode } }),
        api.get("/uploads/recon/summary", { params: { date, shift_code: shiftCode } }),
      ]);
      setRecon(r.data);
      setSummary(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [date, shiftCode]); // eslint-disable-line

  const init = async () => {
    try {
      const r = await api.post("/uploads/recon/init", { date, shift_code: shiftCode });
      setRecon(r.data);
      toast.success("Recon initialized");
      const s = await api.get("/uploads/recon/summary", { params: { date, shift_code: shiftCode } });
      setSummary(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateRow = async (section, row_id, patch) => {
    try {
      const r = await api.patch(`/uploads/recon/${recon.recon_id}/row`, { row_id, section, ...patch });
      setRecon(r.data);
      // refresh summary so CMS slip variance re-computes when PM09 row is typed
      const s = await api.get("/uploads/recon/summary", { params: { date, shift_code: shiftCode } });
      setSummary(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const close = async () => {
    try {
      await api.post("/uploads/recon/close", { recon_id: recon.recon_id });
      toast.success("Recon closed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="recon-root">
      <Toaster position="top-center" />
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Reconciliation</h1>

      {/* CMS lag — this is a real Velkar operations quirk. If cashiers don't
          understand it, they chase phantom variances every day. */}
      <div className="border-2 border-amber-400 bg-amber-50 rounded-sm p-3 text-sm" data-testid="cms-lag-banner">
        <div className="font-bold text-amber-900 flex items-center gap-1">
          <span>⚠</span> Today&rsquo;s CMS deposit is NOT today&rsquo;s cash
        </div>
        <div className="text-amber-900 mt-1">
          The CMS machine collects at <strong>17:00</strong>. Today&rsquo;s slip covers <strong>yesterday
          15:00 → today 07:00 + today 07:00–15:00</strong> — i.e. yesterday&rsquo;s SC-02 + SC-03 + today&rsquo;s
          SC-01. Don&rsquo;t match a same-day slip to same-day expected cash — the variance will look wrong
          when nothing is wrong. Configure the exact mapping in{" "}
          <a href="/config" className="underline font-semibold">Settings → CMS_LAG_MAPPING</a>.
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" data-testid="recon-date" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Shift</label>
          <div className="flex gap-1 mt-0.5">
            {shifts.map((s) => (
              <button key={s} type="button" onClick={() => setShiftCode(s)}
                      className={"px-3 h-11 border-2 font-bold text-sm rounded-sm " + (shiftCode === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}>{s}</button>
            ))}
          </div>
        </div>
        {!recon && <Button data-testid="init-recon-btn" onClick={init} className="bg-slate-900 hover:bg-slate-800 text-white">Initialize Reconciliation</Button>}
        {recon && recon.status !== "CLOSED" && <Button data-testid="close-recon-btn" onClick={close} variant="outline" className="border-slate-300">Mark Closed</Button>}
      </div>

      {summary && <CashDepositPanel s={summary} />}

      {recon && (
        <>
          <Card className="p-3 border-2 border-slate-300 rounded-sm">
            <h3 className="font-bold text-slate-800 mb-2">Payments</h3>
            <div className="overflow-x-auto">
              <table className="dense-table w-full text-sm">
                <thead><tr><th>Mode</th><th>Cat</th><th>System ₹</th><th>PDF ₹</th><th>Diff</th><th>Comment</th></tr></thead>
                <tbody>
                  {recon.payments.map((r) => (
                    <tr key={r.row_id} data-testid={`recon-pay-${r.row_id}`}>
                      <td>{r.pm_id}</td>
                      <td>{r.category}</td>
                      <td className="num">{fmtINR(r.system_amount)}</td>
                      <td><Input type="number" step="0.01" value={r.pdf_amount ?? ""} onChange={(e) => updateRow("payments", r.row_id, { pdf_amount: Number(e.target.value), pdf_source: "MANUAL" })} className="num w-28 h-9 border border-slate-300" /></td>
                      <td className={"num font-bold " + ((r.difference == null) ? "" : Math.abs(r.difference) > 1 ? "text-red-600" : "text-emerald-700")}>{r.difference == null ? "—" : fmtINR(r.difference)}</td>
                      <td><Input value={r.comment || ""} onChange={(e) => updateRow("payments", r.row_id, { comment: e.target.value })} className="h-9 border border-slate-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-3 border-2 border-slate-300 rounded-sm">
            <h3 className="font-bold text-slate-800 mb-2">Debtors</h3>
            <div className="overflow-x-auto">
              <table className="dense-table w-full text-sm">
                <thead><tr><th>Debtor</th><th>System ₹</th><th>PDF ₹</th><th>Diff</th><th>Comments</th></tr></thead>
                <tbody>
                  {recon.debtors.map((r) => (
                    <tr key={r.row_id}>
                      <td>{r.debtor_id}</td>
                      <td className="num">{fmtINR(r.system_amount)}</td>
                      <td><Input type="number" step="0.01" value={r.pdf_amount ?? ""} onChange={(e) => updateRow("debtors", r.row_id, { pdf_amount: Number(e.target.value), pdf_source: "MANUAL" })} className="num w-28 h-9 border border-slate-300" /></td>
                      <td className={"num font-bold " + ((r.difference == null) ? "" : Math.abs(r.difference) > 1 ? "text-red-600" : "text-emerald-700")}>{r.difference == null ? "—" : fmtINR(r.difference)}</td>
                      <td><Input value={r.comments || ""} onChange={(e) => updateRow("debtors", r.row_id, { comment: e.target.value })} className="h-9 border border-slate-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-3 border-2 border-slate-300 rounded-sm">
            <h3 className="font-bold text-slate-800 mb-2">Nozzle Readings</h3>
            <div className="overflow-x-auto">
              <table className="dense-table w-full text-sm">
                <thead><tr><th>Nozzle</th><th>Entered Closing</th><th>Proof Reading</th><th>Match</th><th>Comment</th></tr></thead>
                <tbody>
                  {recon.readings.map((r) => (
                    <tr key={r.row_id}>
                      <td>{r.nozzle_id}</td>
                      <td className="num">{r.entered_closing}</td>
                      <td><Input type="number" step="0.01" value={r.proof_reading ?? ""} onChange={(e) => updateRow("readings", r.row_id, { proof_reading: Number(e.target.value) })} className="num w-32 h-9 border border-slate-300" /></td>
                      <td>
                        <div className="flex gap-1">
                          {["YES", "NO"].map((m) => (
                            <button key={m} type="button" onClick={() => updateRow("readings", r.row_id, { match: m })}
                                    className={"px-2 py-1 border rounded-sm text-xs font-bold " + (r.match === m ? (m === "YES" ? "bg-emerald-600 text-white border-emerald-600" : "bg-red-600 text-white border-red-600") : "bg-white border-slate-300")}>{m}</button>
                          ))}
                        </div>
                      </td>
                      <td><Input value={r.comment || ""} onChange={(e) => updateRow("readings", r.row_id, { comment: e.target.value })} className="h-9 border border-slate-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function CashDepositPanel({ s }) {
  const statusStyle = {
    OK:              { bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-900", icon: <CheckCircle2 className="text-emerald-700" size={20} />, label: "Cash slip matches expected within tolerance" },
    SHORT:           { bg: "bg-red-50",     border: "border-red-500",     text: "text-red-900",     icon: <AlertTriangle className="text-red-700" size={20} />,   label: "SHORT DEPOSIT — cash slip is below expected" },
    OVER:            { bg: "bg-amber-50",   border: "border-amber-500",   text: "text-amber-900",   icon: <AlertTriangle className="text-amber-700" size={20} />, label: "OVER DEPOSIT — cash slip is above expected (investigate)" },
    SLIP_MISSING:    { bg: "bg-red-50",     border: "border-red-500",     text: "text-red-900",     icon: <FileWarning className="text-red-700" size={20} />,    label: "CMS Cash Deposit Slip has not been uploaded" },
    SLIP_UNREADABLE: { bg: "bg-red-50",     border: "border-red-500",     text: "text-red-900",     icon: <FileWarning className="text-red-700" size={20} />,    label: "CMS Cash Deposit Slip is UNREADABLE — ask for a re-upload" },
  }[s.status] || { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-900", icon: null, label: "" };

  const openSlip = () => {
    if (!s.cms_slip_file_id) return;
    window.open(`/api/uploads/file/${s.cms_slip_file_id}`, "_blank");
  };

  return (
    <Card className={"p-4 border-2 rounded-sm " + statusStyle.border + " " + statusStyle.bg} data-testid="cash-deposit-panel">
      <div className="flex items-start gap-3 mb-3">
        <Banknote className="text-slate-900 shrink-0 mt-1" size={24} />
        <div className="flex-1">
          <h2 className={"text-lg font-bold " + statusStyle.text}>Cash Deposit Reconciliation</h2>
          <p className="text-xs text-slate-600 mt-0.5">Total Gas Sales <span className="mx-1">−</span> Digital Payments <span className="mx-1">−</span> Debtor Credit <span className="mx-1">−</span> Adjustments <span className="mx-1">=</span> <span className="font-bold">Expected Cash</span>, matched to the uploaded CMS Cash Deposit Slip.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
        <MetricCell label="Total Gas Sales"    value={s.total_gas_sales}    tone="pos" />
        <MetricCell label="− Digital Payments" value={s.digital_total}      tone="neg" />
        <MetricCell label="− Debtor Credit"    value={s.debtor_total}       tone="neg" />
        <MetricCell label="− Adjustments"      value={s.adjustments_total}  tone="neg" />
        <MetricCell label="+ Lubricants"       value={s.lubricant_total}    tone="pos" />
        <MetricCell label="= Expected Cash"    value={s.expected_cash_total} tone="strong" testId="expected-cash-total" />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
        <div className="bg-white border-2 border-slate-300 rounded-sm p-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Expected Cash (system)</div>
          <div className="num text-2xl font-bold text-slate-900 mt-1" data-testid="expected-cash-panel">{fmtINR(s.expected_cash_total)}</div>
          <div className="text-xs text-slate-500 mt-1">Fuels: {fmtINR(s.expected_cash_fuels)} · CNG: {fmtINR(s.expected_cash_cng)}</div>
        </div>

        <div className="bg-white border-2 border-slate-300 rounded-sm p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">CMS Cash Deposit Slip</div>
            {s.cms_slip_file_id && (
              <button type="button" onClick={openSlip} className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1 underline" data-testid="open-cms-slip">
                <ExternalLink size={12} /> View slip
              </button>
            )}
          </div>
          <div className="num text-2xl font-bold text-slate-900 mt-1" data-testid="cms-slip-amount">
            {s.cms_slip_source === "NONE" ? "—" : fmtINR(s.cms_slip_amount)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Source: <span className="font-bold">{s.cms_slip_source}</span>
            {" · Slot: "}<span className={"font-bold " + (s.cms_slip_status === "UPLOADED" ? "text-emerald-700" : "text-red-700")}>{s.cms_slip_status}</span>
          </div>
        </div>

        <div className={"border-2 rounded-sm p-3 " + statusStyle.border + " bg-white"}>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Variance (slip − expected)</div>
          <div className={"num text-3xl font-bold mt-1 " + (Math.abs(s.variance) > s.tolerance ? "text-red-700" : "text-emerald-700")} data-testid="cash-deposit-variance">
            {s.cms_slip_source === "NONE" ? "—" : fmtINR(s.variance)}
          </div>
          <div className="text-xs mt-1 flex items-start gap-1">
            {statusStyle.icon}
            <span className={statusStyle.text + " font-medium"}>{statusStyle.label}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">Tolerance ±₹{s.tolerance}</div>
        </div>
      </div>

      {s.cms_slip_source === "NONE" && (
        <div className="mt-3 text-xs text-red-800 bg-red-100 border border-red-300 p-2 rounded-sm" data-testid="cms-missing-hint">
          No CMS Cash Deposit Slip found. Upload it under <span className="font-bold">Upload</span> and bind it to the CMS Cash Deposit Receipt row, or type the slip amount into the CMS row in the Payments table below.
        </div>
      )}
    </Card>
  );
}

function MetricCell({ label, value, tone, testId }) {
  const toneCls = tone === "pos" ? "text-slate-900"
                : tone === "neg" ? "text-slate-700"
                : tone === "strong" ? "text-slate-900 font-extrabold"
                : "text-slate-800";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"num text-base font-bold mt-0.5 " + toneCls} data-testid={testId}>{fmtINR(value || 0)}</div>
    </div>
  );
}

